import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';

import { Contact } from 'src/app/models/contact.model';
import * as fromStore from 'src/app/store/';

@Component({
  selector: 'app-primary-reporter',
  templateUrl: './primary-reporter.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PrimaryReporterComponent {
  formRef = input<UntypedFormGroup>();

  inlineErrors = input<Record<string, string | null>>({});

  reporters = input<Contact[]>();

  altContact = input(true);

  usersLoaded = output<boolean>();

  /** Defaults to the current user's contact record; selectReporter() can override it. */
  primaryReporter = linkedSignal(() =>
    this.reporters()?.find((x) => x.Id === this.currentUser()?.Id),
  );

  isLoadingReporters = signal(true);

  isDropdownOpen = signal(false);

  reporterSearchTerm = signal('');

  private store = inject(Store<fromStore.State>);

  private el = inject(ElementRef);

  private currentUser = toSignal(this.store.select(fromStore.getCurrentUser));

  filteredReporters = computed(() => {
    const reporters = this.reporters();
    if (!reporters?.length) return [];
    const searchTerm = this.reporterSearchTerm();
    if (!searchTerm) return reporters;
    const term = searchTerm.toLowerCase();
    return reporters.filter((rep) => rep.Name.toLowerCase().includes(term));
  });

  /** Derived from primaryReporter, not a formRef lookup, so it stays a valid computed(). */
  selectedReporterLabel = computed(() => this.primaryReporter()?.Name ?? '');

  constructor() {
    effect(() => {
      const reporters = this.reporters();
      if (!reporters) return;

      const userId = this.currentUser()?.Id;
      const found = reporters.find((x) => x.Id === userId);
      this.formRef()?.controls.reporter.setValue(found?.Id);

      this.usersLoaded.emit(true);
      this.isLoadingReporters.set(false);
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.el.nativeElement.contains(event.target)) {
      this.isDropdownOpen.set(false);
      this.reporterSearchTerm.set('');
    }
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.isDropdownOpen.update((open) => !open);
    if (!this.isDropdownOpen()) this.reporterSearchTerm.set('');
  }

  selectReporter(rep: Contact, event: Event): void {
    event.stopPropagation();
    this.formRef()?.controls.reporter.setValue(rep.Id);
    this.primaryReporter.set(rep);
    this.isDropdownOpen.set(false);
    this.reporterSearchTerm.set('');
  }

  onSearchInput(event: Event): void {
    this.reporterSearchTerm.set((event.target as HTMLInputElement).value);
  }

  canSubmit() {
    const formRef = this.formRef();
    Object.keys(formRef.controls).forEach((prop) => {
      const control = formRef.controls[prop] as UntypedFormControl;
      control?.updateValueAndValidity();
      this.inlineErrors()[prop] = control.valid
        ? null
        : 'Please complete this field.';
    });
    return formRef.valid;
  }
}
